import { useCallback, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Formik } from 'formik';
import type { FormikHelpers } from 'formik';

import { RightDrawer } from '../../../components/RightDrawer';
import { useIsTouchDevice } from '../../../hooks/useIsTouchDevice.hook';
import { useUiContext } from '../../../contexts/UiContext/hooks/useUiContext.hook';
import { useActionBlocker } from '../../../hooks/useActionBlocker.hook';
import { useMemos } from '../../MemoBeforeSubmit/hooks/useMemos.hook';
import type { SubmitHandler } from '../types/submit-handler.type';

import { ReorderDrawerBody } from './Body';
import {
  buildLabelMaps,
  toReorderDrawerInput,
} from './utils/build-drawer-view.util';
import type { ReorderDrawerGroupView, ReorderDrawerValues } from './types';

export type {
  ReorderDrawerGroupView,
  ReorderDrawerUsageView,
  ReorderDrawerValues,
} from './types';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly groups: readonly ReorderDrawerGroupView[];
  readonly isOpen: boolean;
  readonly onDrawerClose: () => void;
  readonly onSubmit: SubmitHandler<ReorderDrawerValues>;
}

export const ReorderDrawer = ({
  askMemoBeforeSubmit = true,
  groups,
  isOpen,
  onDrawerClose: handleDrawerClose,
  onSubmit: pHandleSubmit,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const isTouchDevice = useIsTouchDevice();

  const [formError, setFormError] = useState<readonly string[] | null>(null);
  const { clearMemos, memos, setMemos } = useMemos();

  const { groupLabelById, usageLabelById } = buildLabelMaps(groups);

  const initialValues: ReorderDrawerValues = {
    groups: toReorderDrawerInput(groups),
  };

  const handleSubmit = useCallback(
    (
      values: ReorderDrawerValues,
      formikHelpers: FormikHelpers<ReorderDrawerValues>,
    ) => {
      setFormError(null);

      void pHandleSubmit({
        formikHelpers,
        memos,
        setFormError,
        values,
      })?.then(clearMemos);
    },
    [clearMemos, memos, pHandleSubmit],
  );

  const [blockableCloseDrawer, setMustBlockAction] =
    useActionBlocker(handleDrawerClose);

  return (
    <RightDrawer isOpen={isOpen} onDrawerClose={blockableCloseDrawer}>
      <DndProvider backend={isTouchDevice ? TouchBackend : HTML5Backend}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({
            handleSubmit: formikSubmit,
            isSubmitting,
            setFieldValue,
            submitForm,
            values,
          }) => (
            <ReorderDrawerBody
              askMemoBeforeSubmit={askMemoBeforeSubmit}
              blockableCloseDrawer={blockableCloseDrawer}
              clearMemos={clearMemos}
              formError={formError}
              groupLabelById={groupLabelById}
              isLoading={isSubmitting || ui.isBackgroundOperationRunning}
              memos={memos}
              setFieldValue={setFieldValue}
              setMemos={setMemos}
              setMustBlockAction={setMustBlockAction}
              submitForm={submitForm}
              submitFormEvent={formikSubmit}
              usageLabelById={usageLabelById}
              values={values}
            />
          )}
        </Formik>
      </DndProvider>
    </RightDrawer>
  );
};
